import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiDelete } from '../../lib/api';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import type { GroupBy } from '../../lib/boardGrouping';

export interface BoardPreset {
  id: string; name: string; group_by: GroupBy; filters: Record<string, unknown>;
  show_empty: boolean; sort_order: number;
}
export interface CurrentBoardState {
  groupBy: GroupBy; filters: Record<string, unknown>; showEmpty: boolean;
}

export function BoardPresetBar({ current, activeId, onApply }: {
  current: CurrentBoardState;
  activeId: string | null;
  onApply: (preset: BoardPreset) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [presets, setPresets] = useState<BoardPreset[]>([]);

  function load() { apiGet<BoardPreset[]>('/board-presets').then(setPresets).catch(() => setPresets([])); }
  useEffect(() => { load(); }, []);

  async function save() {
    const name = window.prompt('Name this board preset:');
    if (!name || !name.trim()) return;
    try {
      await apiPost('/board-presets', {
        name: name.trim(), group_by: current.groupBy,
        filters: current.filters, show_empty: current.showEmpty,
      });
      toast.success('Preset saved');
      load();
    } catch { toast.error('Failed to save preset'); }
  }

  async function remove(p: BoardPreset) {
    if (!await confirm({ title: `Delete preset "${p.name}"?`, variant: 'danger', confirmLabel: 'Delete' })) return;
    try { await apiDelete(`/board-presets/${p.id}`); toast.success('Preset deleted'); load(); }
    catch { toast.error('Failed to delete preset'); }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[11px] uppercase tracking-wide text-text-muted font-semibold">Presets</span>
      {presets.map(p => (
        <span key={p.id} className={`group inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full border text-xs cursor-pointer ${
          activeId === p.id ? 'bg-accent/15 border-accent text-text-primary' : 'bg-bg-surface border-border text-text-secondary hover:border-border-hover'}`}>
          <button onClick={() => onApply(p)}>{p.name}</button>
          <button onClick={() => remove(p)} className="opacity-0 group-hover:opacity-100 text-text-muted hover:text-danger" title="Delete preset">×</button>
        </span>
      ))}
      <button onClick={save} className="px-3 py-1 rounded-full border border-dashed border-border text-xs text-text-muted hover:text-text-primary">
        + Save current
      </button>
    </div>
  );
}
